import { CSSObject } from 'styled-components';
import { TCustomMapControl } from '@hotelplan/fdr.regular.fusion.fdr-map';

const CHECKBOX_ID = `control-checkbox`;

interface INearHotelsControlOptions {
  value: boolean;
  onChange(nextValue: boolean): void;
  title?: string;
}

const controlStyles: CSSObject = {
  display: 'flex',
  height: '40px',
  border: '2px solid #fff',
  marginTop: '10px',
  marginRight: '10px',
  padding: '10px',
  borderRadius: '3px',
  background: 'white',
  boxShadow: '0 2px 6px rgba(0,0,0,.3)',
  cursor: 'pointer',
};

const checkboxStyles: CSSObject = {
  width: '20px',
  height: '20px',
  marginRight: '10px',
  appearance: `checkbox`,
};

const controlContainerStyles: CSSObject = {
  display: 'flex',
  alignItems: 'center',
};

const spinnerStyles: CSSObject = {
  width: '50px',
  height: '20px',
};

function setStyles(styles: CSSObject, el: HTMLElement) {
  Object.entries(styles).forEach(([prop, style]) => {
    el.style[prop] = style;
  });
}

function createControlUIWrapper(): HTMLDivElement {
  const controlUI = document.createElement('div');
  setStyles(controlStyles, controlUI);

  return controlUI;
}

function createCheckbox(
  value: boolean,
  onChange: (nextValue: boolean) => void
): TCustomMapControl {
  const controlCheckbox = document.createElement('input');

  controlCheckbox.setAttribute(`type`, `checkbox`);
  controlCheckbox.setAttribute(`id`, CHECKBOX_ID);

  if (value) {
    controlCheckbox.setAttribute(`checked`, `true`);
  } else {
    controlCheckbox.removeAttribute(`checked`);
  }

  setStyles(checkboxStyles, controlCheckbox);

  function handleOnChange(e: Event) {
    onChange((e.target as HTMLInputElement).checked);
  }

  controlCheckbox.addEventListener('change', handleOnChange);

  return {
    element: controlCheckbox,
    cleanup: () =>
      controlCheckbox.removeEventListener('change', handleOnChange),
  };
}

function createCheckboxLabel(title: string): HTMLLabelElement {
  const controlTitle = document.createElement('label');
  controlTitle.setAttribute(`for`, CHECKBOX_ID);
  controlTitle.innerText = title;

  return controlTitle;
}

function createControl({
  value,
  title,
  onChange,
}: INearHotelsControlOptions): TCustomMapControl {
  const control = document.createElement('div');

  const { element: controlCheckbox, cleanup } = createCheckbox(value, onChange);
  const checkboxLabel = createCheckboxLabel(title);

  setStyles(controlContainerStyles, control);

  control.appendChild(controlCheckbox);
  control.appendChild(checkboxLabel);

  return {
    element: control,
    cleanup,
  };
}

function createSpinner() {
  const spinnerButton = document.createElement(`img`);
  spinnerButton.setAttribute(`src`, `/images/spinner-button.svg`);
  setStyles(spinnerStyles, spinnerButton);

  return spinnerButton;
}

export function createSpinnerControl(): TCustomMapControl {
  if (process.browser) {
    const spinnerControl = document.createElement('div');
    const controlUI = createControlUIWrapper();
    const spinner = createSpinner();

    controlUI.appendChild(spinner);
    spinnerControl.appendChild(controlUI);

    return {
      element: spinnerControl,
    };
  }
}

export function createNearHotelsControl({
  value,
  onChange,
  title,
}: INearHotelsControlOptions): TCustomMapControl {
  if (process.browser) {
    const nearHotelsControl = document.createElement('div');

    const controlUI = createControlUIWrapper();
    const { element: control, cleanup } = createControl({
      value,
      title,
      onChange,
    });

    controlUI.appendChild(control);
    nearHotelsControl.appendChild(controlUI);

    return {
      element: nearHotelsControl,
      cleanup,
    };
  }
}
